/**
 * How long an answer to write.
 *
 * This is the one setting that changes the artefact most, so it is the one
 * control that stays visible. It is a menu rather than four segments because
 * the choice deserves a sentence of explanation each — those sentences already
 * exist in `shared/depth.ts`, and were previously hidden in tooltips where
 * nobody reads them.
 */

import { ChevronDown, Sparkles } from "lucide-react";
import {
  AUTO_DEPTH,
  DEPTHS,
  type DepthChoice,
} from "../../../shared/depth.ts";
import { Button } from "../ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.tsx";

interface LengthMenuProps {
  readonly value: DepthChoice;
  readonly onChange: (value: DepthChoice) => void;
}

/** `auto` first: it is the default, and the one most readers should stay on. */
const OPTIONS: readonly { id: DepthChoice; label: string; blurb: string }[] = [
  AUTO_DEPTH,
  ...DEPTHS.map(({ id, label, blurb }) => ({ id, label, blurb })),
];

export function LengthMenu({ value, onChange }: LengthMenuProps) {
  const selected = OPTIONS.find((option) => option.id === value) ?? AUTO_DEPTH;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={`Answer length: ${selected.label}`}
          className="gap-1.5 font-sans text-xs"
        >
          {value === "auto" && <Sparkles className="text-primary" />}
          {selected.label}
          <ChevronDown className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="max-w-80 font-sans">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => onChange(next as DepthChoice)}
        >
          {OPTIONS.map((option) => (
            <DropdownMenuRadioItem
              key={option.id}
              value={option.id}
              className="items-start gap-2 py-2"
            >
              <span className="flex flex-col gap-0.5">
                <span className="flex items-center gap-1.5 font-medium">
                  {option.id === "auto" && (
                    <Sparkles className="size-3.5 text-primary" />
                  )}
                  {option.label}
                </span>
                <span className="text-xs leading-snug text-muted-foreground">
                  {option.blurb}
                </span>
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
